const chapter = {
  id: 'biological-classification',
  slug: 'biological-classification',
  subject: 'biology',
  subjectLabel: 'Biology',
  exam: 'NEET',
  title: {
    english: 'Biological Classification',
        hindi: 'जीवों का वर्गीकरण',
  },
  estReadMinutes: 50,
  difficulty: {
    english: 'Easy',
        hindi: 'आसान',
  },
  weightage: {
    english: '2-3 questions in NEET',
        hindi: 'NEET में 2-3 प्रश्न',
  },
  syllabusTags: ['Diversity', 'NEET', 'Class-11'],
  sections: [
    {
      id: 'history',
      title: {
        english: 'History of Classification',
        hindi: 'वर्गीकरण का इतिहास',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Aristotle** — earliest scientific basis (plants: herbs/shrubs/trees; animals: with/without red blood).',
        hindi: '**अरस्तू** - प्रारंभिक वैज्ञानिक आधार (पौधे: herbs/shrubs/पेड़; जानवर: with/without लाल रक्त)।',
            },
            {
              english: '**Linnaeus (1758)** — **Two-kingdom system**: Plantae & Animalia. Limitation: did not differentiate eukaryotes/prokaryotes, unicellular/multicellular, photosynthetic green algae vs non-photosynthetic fungi.',
        hindi: '**लिनिअस (1758)** — **दो-राज्य प्रणाली**: प्लांटे और एनिमेलिया। सीमा: eukaryotes/prokaryotes, unicellular/multicellular, प्रकाश संश्लेषक हरे शैवाल बनाम गैर-प्रकाश संश्लेषक कवक में अंतर नहीं किया गया।',
            },
            {
              english: '**Whittaker (1969)** — **Five-kingdom system**: Monera, Protista, Fungi, Plantae, Animalia.',
        hindi: '**व्हिटेकर (1969)** - **पांच-जगत प्रणाली**: मोनेरा, प्रोटिस्टा, फंगी, प्लांटे, एनिमेलिया।',
            },
            {
              english: 'Criteria: cell structure (pro/eu), body organisation (uni/multi), mode of nutrition (auto/hetero), reproduction, phylogenetic relationships.',
        hindi: 'मानदंड: कोशिका संरचना (pro/eu), शारीरिक संगठन (uni/multi), पोषण का तरीका (auto/hetero), प्रजनन, फ़ाइलोजेनेटिक संबंध।',
            },
            {
              english: '**Three-domain system** (Carl Woese, later) — splits Monera into **Archaea** and **Bacteria**, plus Eukarya.',
        hindi: '**तीन-डोमेन प्रणाली** (कार्ल वोइस, बाद में) - मोनेरा को **आर्किया** और **बैक्टीरिया**, प्लस यूकेरिया में विभाजित करता है।',
            },
          ],
        },
        {
          type: 'table',
          headers: ['Feature', 'Monera', 'Protista', 'Fungi', 'Plantae', 'Animalia'],
          rows: [
            ['Cell type', 'Prokaryotic', 'Eukaryotic', 'Eukaryotic', 'Eukaryotic', 'Eukaryotic'],
            ['Cell wall', 'Peptidoglycan', 'Present in some', 'Chitin', 'Cellulose', 'Absent'],
            ['Body', 'Unicellular', 'Unicellular', 'Multicellular (most)', 'Multicellular', 'Multicellular'],
            ['Nutrition', 'Auto/Hetero', 'Auto/Hetero', 'Heterotrophic (saprophytic)', 'Autotrophic', 'Heterotrophic (holozoic)'],
            ['Nuclear membrane', 'Absent', 'Present', 'Present', 'Present', 'Present'],
          ],
        },
      ],
    },
    {
      id: 'monera',
      title: {
        english: 'Kingdom Monera (Bacteria)',
        hindi: 'जगत मोनेरा (जीवाणु)',
      },
      blocks: [
        {
          type: 'paragraph',
          text: {
            english: 'Most abundant organisms on Earth. **Prokaryotic**, lack membrane-bound organelles. Cell wall of peptidoglycan (murein).',
        hindi: 'पृथ्वी पर सर्वाधिक प्रचुर मात्रा में पाए जाने वाले जीव। **प्रोकैरियोटिक**, झिल्ली-बद्ध अंगकों की कमी होती है। पेप्टिडोग्लाइकन (म्यूरिन) की कोशिका भित्ति।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '**Shape-based**: Coccus (spherical), Bacillus (rod), Vibrio (comma), Spirillum (spiral).',
        hindi: '**आकार-आधारित**: कोकस (गोलाकार), बैसिलस (रॉड), विब्रियो (अल्पविराम), स्पिरिलम (सर्पिल)।',
            },
            {
              english: '**Gram staining** (Hans Christian Gram): Gram-positive retain crystal violet (thick peptidoglycan); Gram-negative lose stain (thin peptidoglycan + outer LPS membrane).',
        hindi: '**ग्राम स्टेनिंग** (हंस क्रिश्चियन ग्राम): ग्राम-पॉजिटिव रिटेन क्रिस्टल वायलेट (मोटा पेप्टिडोग्लाइकन); ग्राम-नेगेटिव दाग खो देता है (पतला पेप्टिडोग्लाइकन + बाहरी एलपीएस झिल्ली)।',
            },
            {
              english: '**Archaebacteria** — extremophiles; cell wall lacks peptidoglycan; branched-chain lipids:',
        hindi: '**आर्कबैक्टीरिया** - अतिप्रेमी; कोशिका भित्ति में पेप्टिडोग्लाइकन की कमी होती है; शाखित-श्रृंखला लिपिड:',
            },
            {
              english: '• Halophiles — salt loving (Dead Sea).',
        hindi: '• हेलोफाइल - नमक प्रेमी (मृत सागर)।',
            },
            {
              english: '• Thermoacidophiles — hot sulphur springs.',
        hindi: '• थर्मोएसिडोफिल्स - गर्म सल्फर स्प्रिंग्स।',
            },
            {
              english: '• Methanogens — anaerobic; produce methane in cattle gut & marshes (e.g. Methanobacterium).',
        hindi: '• मेथनोगेंस - अवायवीय; मवेशियों की आंत और दलदल में मीथेन का उत्पादन (जैसे मेथनोबैक्टीरियम)।',
            },
            {
              english: '**Eubacteria** (true bacteria) — rigid peptidoglycan wall; motile by flagella.',
        hindi: '**यूबैक्टीरिया** (असली बैक्टीरिया) - कठोर पेप्टिडोग्लाइकन दीवार; फ्लैगेल्ला द्वारा गतिशील।',
            },
            {
              english: '• Cyanobacteria (blue-green algae) — photosynthetic; chlorophyll-a; **fix atmospheric N₂ in heterocysts** (Nostoc, Anabaena). Form blooms in eutrophic water.',
        hindi: '• साइनोबैक्टीरिया (नीला-हरा शैवाल) - प्रकाश संश्लेषक; क्लोरोफिल-ए; **हेटेरोसिस्ट्स में वायुमंडलीय N₂ को ठीक करें** (नोस्टॉक, एनाबेना)। यूट्रोफिक जल में रूप खिलता है।',
            },
            {
              english: '• Chemosynthetic autotrophs — oxidise inorganic substances (NH₃, NO₂⁻, H₂S, Fe²⁺) for ATP. Recycle N, P, Fe, S.',
        hindi: '• केमोसिंथेटिक ऑटोट्रॉफ़्स - एटीपी के लिए अकार्बनिक पदार्थों (NH₃, NO₂⁻, H₂S, Fe²⁺) का ऑक्सीकरण करते हैं। एन, पी, फे, एस को रीसायकल करें।',
            },
            {
              english: '• Heterotrophic bacteria — most abundant; decomposers; nitrogen fixers (Rhizobium); useful in curd (Lactobacillus), antibiotics, sewage; also pathogens (cholera, typhoid, tetanus, citrus canker).',
        hindi: '• हेटरोट्रॉफ़िक बैक्टीरिया - सबसे प्रचुर मात्रा में; डीकंपोजर; नाइट्रोजन फिक्सर (राइजोबियम); दही (लैक्टोबैसिलस), एंटीबायोटिक्स, मल में उपयोगी; रोगजनक भी (हैजा, टाइफाइड, टेटनस, साइट्रस कैंकर)।',
            },
            {
              english: '**Mycoplasma** — smallest living cells; **lack cell wall**; pleomorphic; can survive without O₂; pathogenic in plants & animals (PPLO).',
        hindi: '**माइकोप्लाज़्मा** - सबसे छोटी जीवित कोशिकाएँ; **कोशिका भित्ति का अभाव**; फुफ्फुसीय; O₂ के बिना जीवित रह सकते हैं; पौधों और जानवरों में रोगजनक (पीपीएलओ)।',
            },
          ],
        },
        {
          type: 'note',
          text: {
            english: 'Reproduction in bacteria: mainly **binary fission**; under stress form **endospores**; sexual = DNA transfer (transformation, transduction, conjugation).',
        hindi: 'बैक्टीरिया में प्रजनन: मुख्य रूप से **बाइनरी विखंडन**; तनाव के तहत **एंडोस्पोर्स** का निर्माण होता है; sexual = DNA transfer (transformation, ट्रांसडक्शन, संयुग्मन)।',
          },
        },
      ],
    },
    {
      id: 'protista',
      title: {
        english: 'Kingdom Protista',
        hindi: 'जगत प्रोटिस्टा',
      },
      blocks: [
        {
          type: 'paragraph',
          text: {
            english: 'Single-celled **eukaryotes**; primarily aquatic; bridge between Monera and complex multicellular kingdoms. Membrane-bound organelles, true nucleus, often flagella/cilia.',
        hindi: 'एककोशिकीय **यूकेरियोट्स**; मुख्य रूप से जलीय; मोनेरा और जटिल बहुकोशिकीय साम्राज्यों के बीच पुल। झिल्ली से बंधे अंगक, सच्चा केंद्रक, अक्सर flagella/cilia।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '**Chrysophytes** — diatoms & golden algae (desmids). Diatoms: cell walls of silica (form **diatomaceous earth**); chief producers in oceans.',
        hindi: '**क्राइसोफाइट्स** - डायटम और गोल्डन शैवाल (डेस्मिड्स)। डायटम: सिलिका की कोशिका दीवारें (**डायटोमेसियस पृथ्वी** का निर्माण); महासागरों में मुख्य उत्पादक।',
            },
            {
              english: '**Dinoflagellates** — marine, photosynthetic; two flagella (one transverse, one longitudinal). Red, yellow, green, brown, blue. Red dinoflagellates (Gonyaulax) cause **red tides** → toxin kills fish.',
        hindi: '**डाइनोफ्लैगलेट्स** - समुद्री, प्रकाश संश्लेषक; दो कशाभिका (एक अनुप्रस्थ, एक अनुदैर्ध्य)। लाल, पीला, हरा, भूरा, नीला। लाल डायनोफ्लैगलेट्स (गोन्युलैक्स) **लाल ज्वार** का कारण बनता है → विष मछली को मार देता है।',
            },
            {
              english: '**Euglenoids** (Euglena) — fresh water; flexible **pellicle** instead of cell wall; photosynthetic in light, heterotrophic in dark (mixotrophic). Pigments identical to higher plants.',
        hindi: '**यूग्लेनोइड्स** (यूग्लेना) - ताजा पानी; कोशिका भित्ति के स्थान पर लचीली ** पेलिकल**; प्रकाश में प्रकाश संश्लेषक, अंधेरे में विषमपोषी (मिक्सोट्रोफिक)। उच्च पौधों के समान वर्णक।',
            },
            {
              english: '**Slime moulds** — saprophytic. Form **plasmodium** (multinucleate mass) under suitable conditions; spores extremely resistant.',
        hindi: '** कीचड़ के सांचे** - सैप्रोफाइटिक। उपयुक्त परिस्थितियों में **प्लाज्मोडियम** (मल्टीन्यूक्लिएट द्रव्यमान) का निर्माण; बीजाणु अत्यंत प्रतिरोधी। **प्रोटोज़ोअन** - विषमपोषी शिकारी/परजीवी। चार समूह:',
            },
            {
              english: '**Protozoans** — heterotrophic predators / parasites. Four groups:',
        hindi: '**प्रोटोज़ोअन** - विषमपोषी शिकारी/परजीवी। चार समूह:',
            },
            {
              english: '• Amoeboid — Amoeba, Entamoeba (dysentery); pseudopodia.',
        hindi: '• अमीबॉइड - अमीबा, एंटअमीबा (पेचिश); स्यूडोपोडिया।',
            },
            {
              english: '• Flagellated — Trypanosoma (sleeping sickness), Leishmania.',
        hindi: '• फ्लैगेलेटेड - ट्रिपैनोसोमा (नींद की बीमारी), लीशमैनिया।',
            },
            {
              english: '• Ciliated — Paramoecium; cilia + gullet.',
        hindi: '• सिलिअटेड - पैरामोइकियम; सिलिया + गुलेट.',
            },
            {
              english: '• Sporozoans — Plasmodium (malaria); infectious spore stage.',
        hindi: '• स्पोरोज़ोअन - प्लास्मोडियम (मलेरिया); संक्रामक बीजाणु अवस्था. **हेटरोट्रॉफिक, यूकेरियोटिक, बहुकोशिकीय** (खमीर को छोड़कर - एककोशिकीय)। **चिटिन और पॉलीसेकेराइड** की कोशिका भित्ति। **हाइफ़े** → मायसेलियम का फिलामेंटस शरीर। हाइफ़े कोएनोसाइटिक (एसेप्टेट, मल्टीन्यूक्लिएट) या सेप्टेट हो सकता है।',
            },
          ],
        },
      ],
    },
    {
      id: 'fungi',
      title: {
        english: 'Kingdom Fungi',
        hindi: 'जगत कवक',
      },
      blocks: [
        {
          type: 'paragraph',
          text: {
            english: '**Heterotrophic, eukaryotic, multicellular** (except yeast — unicellular). Cell wall of **chitin & polysaccharides**. Filamentous body of **hyphae** → mycelium. Hyphae may be coenocytic (aseptate, multinucleate) or septate.',
        hindi: '**हेटरोट्रॉफिक, यूकेरियोटिक, बहुकोशिकीय** (खमीर को छोड़कर - एककोशिकीय)। **चिटिन और पॉलीसेकेराइड** की कोशिका भित्ति। **हाइफ़े** → मायसेलियम का फिलामेंटस शरीर। हाइफ़े कोएनोसाइटिक (एसेप्टेट, मल्टीन्यूक्लिएट) या सेप्टेट हो सकता है।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: 'Mode of nutrition: **saprophytic** (dead matter), **parasitic** (Puccinia rust on wheat), **symbiotic** (lichens with algae; mycorrhiza with plant roots).',
        hindi: 'पोषण का तरीका: **सैप्रोफाइटिक** (मृत पदार्थ), **परजीवी** (गेहूं पर पुकिनिया जंग), **सहजीवी** (शैवाल के साथ लाइकेन; पौधों की जड़ों के साथ माइकोराइजा)।',
            },
            {
              english: 'Reproduction: vegetative (fragmentation, budding); asexual (conidia, sporangiospores, zoospores); sexual — three stages: **plasmogamy → karyogamy → meiosis**. Sometimes intervening **dikaryotic phase (n+n)**.',
        hindi: 'प्रजनन: वानस्पतिक (विखंडन, नवोदित); अलैंगिक (कोनिडिया, स्पोरैंगियोस्पोर्स, ज़ोस्पोरेस); यौन - तीन चरण: **प्लाज्मोगैमी → कैरियोगैमी → अर्धसूत्रीविभाजन**। कभी-कभी मध्यवर्ती **डाइकार्योटिक चरण (एन+एन)**।',
            },
            {
              english: '**Phycomycetes** (lower fungi) — aseptate coenocytic hyphae. Asexual zoospores / aplanospores in sporangium. Sexual zygospore. Examples: Mucor, Rhizopus (bread mould), Albugo (mustard parasite).',
        hindi: '**फ़ाइकोमाइसेट्स** (निचला कवक) - एसेप्टेट कोएनोसाइटिक हाइफ़े। स्पोरैन्जियम में अलैंगिक जूस्पोर्स/एप्लानोस्पोर्स। यौन जाइगोस्पोर. उदाहरण: म्यूकर, राइजोपस (ब्रेड मोल्ड), अल्बुगो (सरसों परजीवी)।',
            },
            {
              english: '**Ascomycetes** (sac fungi) — septate, branched. Sexual spores **ascospores** in **ascus**. Asexual conidia. Examples: Saccharomyces (yeast), Penicillium, Aspergillus, Neurospora (genetics), Claviceps (ergot), morels & truffles (edible).',
        hindi: '**एस्कोमाइसेट्स** (थैली कवक) - सेप्टेट, शाखायुक्त। **एस्कस** में यौन बीजाणु **एस्कोस्पोर्स**। अलैंगिक कोनिडिया. उदाहरण: सैक्रोमाइसेस (खमीर), पेनिसिलियम, एस्परगिलस, न्यूरोस्पोरा (जेनेटिक्स), क्लैविसेप्स (एर्गोट), मोरेल और ट्रफल्स (खाद्य)।',
            },
            {
              english: '**Basidiomycetes** (club fungi) — septate hyphae; **basidiospores** on basidium. Sexual reproduction lacks distinct sex organs; plasmogamy by fusion of vegetative cells of different strains. Examples: Agaricus (mushroom), Ustilago (smut), Puccinia (rust).',
        hindi: '**बेसिडिओमाइसीस** (क्लब कवक) - सेप्टेट हाइपहे; बेसिडियम पर **बेसिडियोस्पोर**। यौन प्रजनन में विशिष्ट यौन अंगों का अभाव होता है; विभिन्न उपभेदों की वनस्पति कोशिकाओं के संलयन द्वारा प्लास्मोगैमी। उदाहरण: एगारिकस (मशरूम), उस्टिलैगो (स्मट), पुकिनिया (जंग)।',
            },
            {
              english: '**Deuteromycetes** (Fungi Imperfecti) — only **asexual stage known** (conidia); when sexual stage discovered, reclassified. Examples: Alternaria, Colletotrichum, Trichoderma.',
        hindi: '**ड्यूटेरोमाइसेट्स** (फंगी इम्परफेक्टी) - केवल **अलैंगिक चरण ज्ञात** (कोनिडिया); जब यौन अवस्था का पता चला, तो उसे पुनः वर्गीकृत किया गया। उदाहरण: अल्टरनेरिया, कोलेटोट्राइकम, ट्राइकोडर्मा।',
            },
          ],
        },
      ],
    },
    {
      id: 'plantae-animalia-virus',
      title: {
        english: 'Plantae, Animalia & Acellular Entities',
        hindi: 'पादप, प्राणि और अकोशिकीय जीव',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Plantae** — eukaryotic, chlorophyll-containing autotrophs; cell wall of **cellulose**. Includes algae, bryophytes, pteridophytes, gymnosperms, angiosperms. Some partial heterotrophs (Venus flytrap, Cuscuta).',
        hindi: '**प्लांटे** - यूकेरियोटिक, क्लोरोफिल युक्त स्वपोषी; **सेलूलोज़** की कोशिका भित्ति। इसमें शैवाल, ब्रायोफाइट्स, टेरिडोफाइट्स, जिम्नोस्पर्म, एंजियोस्पर्म शामिल हैं। कुछ आंशिक हेटरोट्रॉफ़्स (वीनस फ्लाईट्रैप, कुस्कुटा)।',
            },
            {
              english: '**Animalia** — multicellular eukaryotic heterotrophs (holozoic — ingest food); no cell wall; sexual reproduction by gamete fusion; show growth pattern up to maturity.',
        hindi: '**एनिमलिया** - बहुकोशिकीय यूकेरियोटिक हेटरोट्रॉफ़्स (होलोज़ोइक - भोजन निगलना); कोई कोशिका भित्ति नहीं; युग्मक संलयन द्वारा यौन प्रजनन; परिपक्वता तक विकास पैटर्न दिखाएं।',
            },
            {
              english: '**Viruses** — non-cellular; obligate intracellular parasites. Outside host: inert crystals; inside: replicate. Genetic material **DNA or RNA, never both**. Plant viruses: ssRNA (TMV). Animal viruses: ssRNA / dsRNA / DNA. Bacteriophages: dsDNA.',
        hindi: '**वायरस** — गैर-सेलुलर; इंट्रासेल्युलर परजीवियों को बाध्य करें। बाहरी मेजबान: निष्क्रिय क्रिस्टल; अंदर: दोहराएँ। आनुवंशिक सामग्री **डीएनए या आरएनए, दोनों कभी नहीं**। पादप विषाणु: ssRNA (TMV)। पशु वायरस: एसएसआरएनए/डीएसआरएनए/डीएनए। बैक्टीरियोफेज: डीएसडीएनए।',
            },
            {
              english: '• Discovered: Ivanowsky (1892) — TMV juice infectious. Beijerinck — "contagium vivum fluidum". Stanley (1935) — crystallised TMV.',
        hindi: '• खोजा गया: इवानोव्स्की (1892) - टीएमवी रस संक्रामक। बेजरिनक - "कॉन्टैगियम विवम फ्लुइडम"। स्टेनली (1935) - क्रिस्टलीकृत टीएमवी।',
            },
            {
              english: 'Diseases: mumps, smallpox, herpes, AIDS, COVID-19.',
        hindi: 'रोग: कण्ठमाला, चेचक, दाद, एड्स, सीओवीआईडी-19।',
            },
            {
              english: '**Viroids** — Diener (1971); free RNA, no protein coat; potato spindle tuber disease.',
        hindi: '**वाइरोइड्स** - डायनर (1971); मुफ़्त आरएनए, कोई प्रोटीन कोट नहीं; आलू स्पिंडल कंद रोग.',
            },
            {
              english: '**Prions** — Stanley Prusiner; infectious **proteins** (no nucleic acid); BSE (mad cow), CJD in humans.',
        hindi: '**प्रियन्स** - स्टेनली प्रूसिनर; संक्रामक **प्रोटीन** (कोई न्यूक्लिक एसिड नहीं); मनुष्यों में बीएसई (पागल गाय), सीजेडी।',
            },
            {
              english: '**Lichens** — symbiotic association: alga (phycobiont — autotroph) + fungus (mycobiont — shelter, water). Excellent **pollution indicators** (do not grow in polluted areas).',
        hindi: '**लाइकेन** - सहजीवी संघ: शैवाल (फ़ाइकोबायंट - ऑटोट्रॉफ़) + कवक (माइकोबायंट - आश्रय, पानी)। उत्कृष्ट **प्रदूषण संकेतक** (प्रदूषित क्षेत्रों में न बढ़ें)।',
            },
          ],
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: '\\text{Whittaker (1969): 5 kingdoms} = \\{Monera, Protista, Fungi, Plantae, Animalia\\}',
      desc: {
        english: '',
        hindi: 'व्हिटेकर (1969): 5 जगत = \\ मोनेरा, प्रोटिस्टा, फंगी, प्लांटे, एनिमेलिया\\',
      },
    },
    {
      latex: '\\text{Bacterial cell wall: peptidoglycan (murein)}',
      desc: {
        english: '',
        hindi: 'जीवाणु कोशिका भित्ति: पेप्टिडोग्लाइकन (म्यूरिन)',
      },
    },
    {
      latex: '\\text{Fungal cell wall: chitin}',
      desc: {
        english: '',
        hindi: 'कवक कोशिका भित्ति: काइटिन',
      },
    },
    {
      latex: '\\text{Fungal sex cycle: plasmogamy} \\to \\text{karyogamy} \\to \\text{meiosis}',
      desc: {
        english: '',
        hindi: 'फंगल सेक्स चक्र: प्लास्मोगैमी कैरियोगैमी अर्धसूत्रीविभाजन',
      },
    },
    {
      latex: 'N_2 + 8H^+ + 8e^- \\to 2NH_3 + H_2',
      desc: {
        english: 'Biological N₂ fixation in heterocysts',
        hindi: 'हेटेरोसिस्ट्स में जैविक एन₂ निर्धारण',
      },
    },
    {
      latex: '\\text{Mycoplasma: smallest cell, no cell wall}',
      desc: {
        english: '',
        hindi: 'माइकोप्लाज्मा: सबसे छोटी कोशिका, कोई कोशिका भित्ति नहीं',
      },
    },
    {
      latex: '\\text{Virus genome: DNA OR RNA (never both)}',
      desc: {
        english: '',
        hindi: 'वायरस जीनोम: डीएनए या आरएनए (दोनों कभी नहीं)',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'NEET',
      tag: 'Monera',
      problem: {
        english: 'Heterocysts in Nostoc are specialised for:',
        hindi: 'नोस्टॉक में हेटेरोसिस्ट इसके लिए विशिष्ट हैं:',
      },
      answer: {
        english: 'Nitrogen fixation',
        hindi: 'नाइट्रोजन स्थिरीकरण',
      },
      solution: {
        english: 'Nitrogenase enzyme protected from O₂ in heterocyst.',
        hindi: 'नाइट्रोजनेज एंजाइम हेटेरोसिस्ट में O₂ से सुरक्षित रहता है।',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Fungi',
      problem: {
        english: 'Cell wall of fungi is mainly composed of:',
        hindi: 'कवक की कोशिका भित्ति मुख्य रूप से बनी होती है:',
      },
      answer: {
        english: 'Chitin',
        hindi: 'चिटिन',
      },
      solution: {
        english: 'Polymer of N-acetylglucosamine.',
        hindi: 'एन-एसिटाइलग्लुकोसामाइन का पॉलिमर।',
      },
    },
    {
      year: 2022,
      exam: 'NEET',
      tag: 'Protista',
      problem: {
        english: 'Red tides in coastal waters are caused by:',
        hindi: 'तटीय जल में लाल ज्वार किसके कारण होते हैं:',
      },
      answer: {
        english: 'Dinoflagellates (Gonyaulax)',
        hindi: 'डिनोफ्लैगलेट्स (गोन्युलैक्स)',
      },
      solution: {
        english: 'Toxic blooms.',
        hindi: 'विषाक्त फूल।',
      },
    },
    {
      year: 2021,
      exam: 'NEET',
      tag: 'Virus',
      problem: {
        english: 'Crystalline form of TMV was first isolated by:',
        hindi: 'TMV के क्रिस्टलीय रूप को सबसे पहले अलग किया गया था:',
      },
      answer: {
        english: 'W.M. Stanley (1935)',
        hindi: 'डब्ल्यू.एम. स्टेनली (1935)',
      },
      solution: {
        english: 'Showed virus could be crystallised.',
        hindi: 'दिखाया गया कि वायरस को क्रिस्टलीकृत किया जा सकता है।',
      },
    },
    {
      year: 2020,
      exam: 'NEET',
      tag: 'Lichen',
      problem: {
        english: 'Lichen is a symbiotic association between:',
        hindi: 'लाइकेन इनके बीच एक सहजीवी संबंध है:',
      },
      answer: {
        english: 'Alga (phycobiont) and fungus (mycobiont)',
        hindi: 'शैवाल (फ़ाइकोबियोन्ट) और कवक (माइकोबियोन्ट)',
      },
      solution: {
        english: 'Mutualism — alga provides food, fungus shelter.',
        hindi: 'पारस्परिकता - शैवाल भोजन, कवक आश्रय प्रदान करता है।',
      },
    },
  ],
  next: {
    slug: 'cell-unit-of-life',
    title: 'Cell — The Unit of Life',
  },
};

export default chapter;
